import React, { useEffect, useState } from "react";
import { collection, query, where, orderBy, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, fns } from "../firebase";
import { useSession } from "../auth.jsx";
import { Eyebrow } from "../components/Bits.jsx";

const CHECKPOINT_COPY = {
  matriculation: "Matriculation baseline",
  midterm: "Midterm · week 12",
  completion: "Completion · week 24",
};

export default function Mindset() {
  const s = useSession();
  const [history, setHistory] = useState([]);
  const [session, setSession] = useState(null); // { sessionId, checkpoint, questions }
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!s.user) return;
    const q = query(collection(db, "mq"), where("residentUid", "==", s.user.uid), orderBy("assessedAt", "asc"));
    return onSnapshot(q, (snap) => setHistory(snap.docs.map((d) => d.data())));
  }, [s.user]);

  const begin = async () => {
    setBusy("start"); setMsg(null); setResult(null);
    try {
      const call = httpsCallable(fns, "startMqSession");
      const res = await call({});
      setSession(res.data);
      setAnswers({});
    } catch (e) { setMsg(e.message); }
    finally { setBusy(""); }
  };

  const submit = async () => {
    setBusy("score"); setMsg(null);
    try {
      const call = httpsCallable(fns, "scoreMq");
      const res = await call({ sessionId: session.sessionId, answers });
      setResult(res.data.profile);
      setSession(null);
    } catch (e) { setMsg(e.message); }
    finally { setBusy(""); }
  };

  const total = session ? session.questions.reduce((n, g) => n + g.questions.length, 0) : 0;
  const done = Object.keys(answers).length;

  return (
    <div style={{ maxWidth: 820 }}>
      <Eyebrow>The Ready Mindset · MQ</Eyebrow>
      <h1 style={{ fontSize: 24, marginTop: 4 }}>Your readiness, measured three times</h1>
      <p style={{ color: "var(--graphite)", maxWidth: "64ch" }}>
        Forty statements, eight barrier groups, about ten minutes. You take it at Matriculation, week 12,
        and week 24. Your full profile is yours; your Studio Master sees the profile name and top barriers —
        enough to support you, and it never touches a mark.
      </p>

      {history.length > 0 && !session && (
        <div className="card" style={{ margin: "16px 0" }}>
          <Eyebrow>Your checkpoints</Eyebrow>
          <table style={{ marginTop: 8 }}>
            <thead><tr><th>Checkpoint</th><th>MQ</th><th>Level</th><th>Profile</th></tr></thead>
            <tbody>
              {history.map((h, i) => (
                <tr key={i}>
                  <td>{CHECKPOINT_COPY[h.checkpoint] ?? h.checkpoint}</td>
                  <td className="mono">{h.mqScore}</td>
                  <td>{h.mqLevel}</td>
                  <td>{h.profileName ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {result && (
        <div className="card" style={{ margin: "16px 0", borderColor: "var(--verdigris)" }}>
          <Eyebrow style={{ color: "var(--verdigris)" }}>Your profile</Eyebrow>
          <h2 style={{ fontSize: 20, marginTop: 6 }}>{result.barrier_profile?.name}</h2>
          <p style={{ color: "var(--graphite)", fontSize: 13 }}>Cluster: {result.barrier_profile?.cluster} · MQ {result.mq_score} · {result.mq_level}</p>
          <p style={{ marginTop: 8 }}>{result.barrier_profile?.insight}</p>
          <Eyebrow style={{ marginTop: 12 }}>Where to put your attention first</Eyebrow>
          <ul style={{ paddingLeft: 18, marginTop: 6 }}>
            {(result.top_3_barriers ?? []).map((b) => <li key={b.idx}>{b.name}</li>)}
          </ul>
          {result.primary_barrier?.tool && (
            <p style={{ fontSize: 14, marginTop: 8 }}>
              Your primary tool: <strong>{result.primary_barrier.tool}</strong> — it anchors phase one of your 90-day pathway.
            </p>
          )}
        </div>
      )}

      {!session ? (
        <button className="btn" onClick={begin} disabled={busy === "start" || history.length >= 3}>
          {busy === "start" ? "Preparing…"
            : history.length >= 3 ? "All three checkpoints complete"
            : history.length === 0 ? "Take the Matriculation baseline"
            : `Take the ${CHECKPOINT_COPY[history.length === 1 ? "midterm" : "completion"]}`}
        </button>
      ) : (
        <div style={{ marginTop: 16 }}>
          <div className="row between">
            <Eyebrow>{CHECKPOINT_COPY[session.checkpoint]}</Eyebrow>
            <span className="mono" style={{ fontSize: 12 }}>{done}/{total}</span>
          </div>
          <p style={{ fontSize: 13, color: "var(--graphite)" }}>
            1 = not at all like me · 5 = exactly like me. Answer honestly — this is for you, not for a grade.
          </p>
          {session.questions.map((group) => (
            <div className="card" key={group.id} style={{ marginTop: 12 }}>
              <Eyebrow>{group.name}</Eyebrow>
              {group.questions.map((q) => (
                <div className="crit" key={q.id}>
                  <p style={{ margin: "0 0 6px" }}>{q.text}</p>
                  <div className="scale" role="radiogroup" aria-label={q.text}>
                    {[1, 2, 3, 4, 5].map((v) => (
                      <button key={v} aria-pressed={answers[q.id] === v}
                        onClick={() => setAnswers({ ...answers, [q.id]: v })}>{v}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
          {msg && <div className="warn" style={{ marginTop: 12 }}>{msg}</div>}
          <button className="btn" style={{ marginTop: 14 }} disabled={done < total || busy === "score"} onClick={submit}>
            {busy === "score" ? "Scoring…" : done < total ? `${total - done} to go` : "Submit"}
          </button>
        </div>
      )}
      {msg && !session && <div className="warn" style={{ marginTop: 12 }}>{msg}</div>}
    </div>
  );
}
